import React, { memo } from 'react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { ExternalLink, Github, BookOpen, Plus, ArrowRight } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { translations } from '../i18n/translations';
// ─────────────────────────────────────────────
// ✏️  AJOUTER / MODIFIER UN PROJET ICI
// ─────────────────────────────────────────────
interface Project {
  en: {
    title: string;
    category: string;
    description: string;
  };
  fr: {
    title: string;
    category: string;
    description: string;
  };
  tags: string[];
  /** URL de l'image de couverture (laisser vide pour utiliser le dégradé) */
  imageUrl?: string;
  /** Dégradé CSS utilisé si pas d'imageUrl */
  imageFallback?: string;
  /** Lien vers la démo / itch.io / build. Si absent, le bouton Demo est masqué. */
  demoUrl?: string;
  /** Lien vers le repo GitHub. Si absent, le bouton Code est masqué. */
  codeUrl?: string;
  /** Mettre true pour afficher la carte "prochain projet" */
  isPlaceholder?: boolean;
}
const PROJECTS: Project[] = [
// ── Projet 1 ──────────────────────────────
{
  en: {
    title: 'Golden Georges',
    category: 'Unity 3D Mobile Game',
    description: "Golden Georges is a game of juggling and skill based on the story of George, an African youth who dreams of becoming a Ballon d'Or soccer player, like George Weah."
  },
  fr: {
    title: 'Golden Georges',
    category: 'Jeu Mobile Unity 3D',
    description: "Golden Georges est un jeu de jongles et d\'adresse qui s'appuie sur l\'histoire Georges, un jeune africain qui rêve de devenir Ballon d\'Or de foot, comme George Weah."
  },
  tags: ['Unity', 'C#', 'Mobile', 'Singleplayer'],
  imageFallback: 'linear-gradient(to bottom right, #1a4fd6, #000000)',
  imageUrl: 'https://res.cloudinary.com/dbl7f0vfr/image/upload/v1772037804/GoldenGeorges_vzo0ud.jpg',
  demoUrl: 'https://play.google.com/store/apps/details?id=com.masseka.GoldenGeorges&hl=fr&pli=1',
  codeUrl: '' // ← mettre l'URL du repo ici (laisser vide = bouton masqué)
},
// ── Projet 2 ──────────────────────────────
{
  en: {
    title: 'Bible Quiz Game',
    category: 'Unity 3D Mobile Game',
    description: 'Discover an exciting Bible quiz game, designed to test your Bible knowledge like never before !'
  },
  fr: {
    title: 'Bible Quiz Game',
    category: 'Jeu Mobile Unity 3D',
    description: 'Découvrez un passionnant jeu de quiz sur la Bible, conçu pour tester vos connaissances bibliques comme jamais auparavant !'
  },
  tags: ['Unity', 'C#', 'Mobile', 'Singleplayer'],
  imageFallback: 'linear-gradient(to bottom right, #1a4fd6, #000000)',
  imageUrl: 'https://res.cloudinary.com/dbl7f0vfr/image/upload/v1772038216/BibleQuizGame_ziaq6c.png',
  demoUrl: 'https://play.google.com/store/apps/details?id=com.masseka.BibleQuizzGame&hl=fr',
  codeUrl: '' // ← mettre l'URL du repo ici (laisser vide = bouton masqué)
},
// ── Projet 3 ──────────────────────────────
{
  en: {
    title: 'A SURVIVOR',
    category: 'Unity 3D Mobile Game',
    description: 'Immerse yourself in the ruthless world of A Survivor. Face relentless waves of enemies in this intense survival in top view. Play as a brave character determined to survive at all costs by collecting upgrades and boosts from defeated enemies.'
  },
  fr: {
    title: 'A SURVIVOR',
    category: 'Jeu Mobile Unity 3D',
    description: "Plongez dans l'univers impitoyable d'A Survivor. Affrontez des vagues incessantes d\'ennemis dans ce survival intense en vue de dessus. Incarnez un courageux personnage déterminé à survivre coûte que coûte en récupérant des améliorations et des boosts sur les ennemis vaincus."
  },
  tags: ['Unity', 'C#', 'Mobile', 'Singleplayer'],
  imageFallback: 'linear-gradient(to bottom right, #1a4fd6, #000000)',
  imageUrl: 'https://res.cloudinary.com/dbl7f0vfr/image/upload/v1772038571/ASurvivor_uxqv1h.png',
  demoUrl: 'https://play.google.com/store/apps/details?id=com.okodev.apocalypsesurvivor',
  codeUrl: 'https://github.com/VincentQvy/AlainSurvivor' // ← mettre l'URL du repo ici (laisser vide = bouton masqué)
},
// ── Projet 4 ──────────────────────────────
{
  en: {
    title: 'HOOKBALL',
    category: 'UE4 Local Multiplayer Battle Arena',
    description: 'Welcome challengers for the annual competition of HookBall. In this racing/battle game, you will compete in an arena with other players in order to be the one to claim the maximum of points.'
  },
  fr: {
    title: 'HOOKBALL',
    category: 'Arène de Bataille Multijoueur Local UE4',
    description: "Bienvenue Challengers pour la compétition annuelle de HookBall. Dans ce jeu de course/combat, vous combattrez dans une arène avec d'autres joueurs pour récolter un maximum de points."
  },
  tags: ['UE4', 'Blueprint', 'Multiplayer', 'Local'],
  imageFallback: 'linear-gradient(to bottom right, #00c9a7, #000000)',
  imageUrl: 'https://res.cloudinary.com/dbl7f0vfr/image/upload/v1772027970/8EDMFe_g0lkky.png',
  demoUrl: 'https://phobosrandom.itch.io/hookball-arena',
  codeUrl: 'https://gitlab.com/Ryfast/hookball.git' // ← mettre l'URL du repo ici (laisser vide = bouton masqué)
},
// ── Carte "prochain projet" (toujours en dernier) ──
{
  en: {
    title: 'Your Next Project',
    category: 'Coming Soon',
    description: "This slot is waiting for your game. Got a concept that needs a gameplay programmer? Let's build something memorable together."
  },
  fr: {
    title: 'Votre Prochain Projet',
    category: 'Bientôt disponible',
    description: 'Cet emplacement attend votre jeu. Vous avez un concept qui nécessite un programmeur gameplay ? Construisons quelque chose de mémorable ensemble.'
  },
  tags: [],
  isPlaceholder: true
}];
// ─────────────────────────────────────────────
export function ProjectsSection() {
  const {
    language
  } = useLanguage();
  const t = translations[language].projects;
  return <section id="projects" className="py-24 bg-[#080816]" data-id="element-178">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-179">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4" data-id="element-180">
          <div data-id="element-181">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-182">{t.title}</h2>
            <p className="text-gray-400 max-w-xl" data-id="element-183">{t.subtitle}</p>
          </div>
          <div className="flex gap-3 flex-shrink-0" data-id="element-184">
            <Button variant="primary" size="md" onClick={() => window.open('https://julienmachado.framer.website', '_blank')} data-id="element-185">
              <BookOpen size={18} className="mr-2 flex-shrink-0" data-id="element-186" />
              <span className="whitespace-nowrap" data-id="element-187">{t.portfolio} ↗</span>
            </Button>
            <Button variant="secondary" size="md" onClick={() => window.open('https://github.com/Saturn1B', '_blank')} data-id="element-188">
              <Github size={18} className="mr-2 flex-shrink-0" data-id="element-189" />
              <span className="whitespace-nowrap" data-id="element-190">{t.github} ↗</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-191">
          {PROJECTS.map((project, index) => {
          const text = project[language];
          if (project.isPlaceholder) {
            return <div key={index} className="rounded-xl border-2 border-dashed border-white/10 bg-[#0a0a1a]/50 flex flex-col items-center justify-center p-10 text-center min-h-[380px] group hover:border-[#00f5a0]/30 transition-all duration-500" data-id="element-192">
                  <div className="w-16 h-16 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center mb-6 group-hover:border-[#00f5a0]/50 transition-colors" data-id="element-193">
                    <Plus size={28} className="text-white/20 group-hover:text-[#00f5a0]/60 transition-colors" data-id="element-194" />
                  </div>
                  <h3 className="text-xl font-bold text-white/40 group-hover:text-white/70 transition-colors mb-3" data-id="element-195">
                    {text.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 group-hover:text-gray-400 transition-colors" data-id="element-196">
                    {text.description}
                  </p>
                  <Button size="sm" variant="outline" onClick={() => document.getElementById('contact')?.scrollIntoView({
                behavior: 'smooth'
              })} className="opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-197">
                    {t.letsTalk} <ArrowRight size={14} className="ml-2" data-id="element-198" />
                  </Button>
                </div>;
          }
          const hasDemo = !!project.demoUrl;
          const hasCode = !!project.codeUrl;
          return <Card key={index} className="group overflow-hidden p-0 border-0 bg-[#0a0a1a]" data-id="element-199">
                <div className="h-48 w-full relative overflow-hidden" style={{
              background: project.imageUrl ? 'transparent' : project.imageFallback
            }} data-id="element-200">
                  {project.imageUrl && <>
                      <img src={project.imageUrl} alt={text.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-201" />
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-300" data-id="element-202"></div>
                    </>}
                  <div className={`absolute inset-0 ${project.imageUrl ? '' : 'bg-black/20'} group-hover:bg-transparent transition-colors duration-300`} data-id="element-203"></div>
                  <div className="absolute bottom-4 left-4 z-10" data-id="element-204">
                    <span className="px-2 py-1 text-xs font-bold bg-black/60 backdrop-blur-sm rounded text-white border border-white/10" data-id="element-205">
                      {text.category}
                    </span>
                  </div>
                </div>

                <div className="p-6" data-id="element-206">
                  <h3 className="text-xl font-bold mb-2 group-hover:text-[#00f5a0] transition-colors" data-id="element-207">
                    {text.title}
                  </h3>
                  <div className="overflow-hidden max-h-[4.5rem] group-hover:max-h-96 transition-all duration-500 ease-in-out mb-4" data-id="element-208">
                    <p className="text-gray-400 text-sm leading-relaxed" data-id="element-209">
                      {text.description}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-210">
                    {project.tags.map(tag => <span key={tag} className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded" data-id="element-211">
                        {tag}
                      </span>)}
                  </div>

                  {(hasDemo || hasCode) && <div className="flex gap-3" data-id="element-212">
                      {hasDemo && <Button size="sm" variant="secondary" className={hasCode ? 'w-full' : 'w-full'} onClick={() => window.open(project.demoUrl, '_blank')} data-id="element-213">
                          <ExternalLink size={14} className="mr-2" data-id="element-214" /> {t.demo}
                        </Button>}
                      {hasCode && <Button size="sm" variant="outline" className={hasDemo ? 'w-full' : 'w-full'} onClick={() => window.open(project.codeUrl, '_blank')} data-id="element-215">
                          <Github size={14} className="mr-2" data-id="element-216" /> {t.code}
                        </Button>}
                    </div>}
                </div>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}